/* eslint-disable no-unused-vars */

import moment from 'moment';
import _ from 'lodash';

import { removeUnavailableTreeData } from '@components/tree-component';
import { searchPipelineData } from '@utilities/pipeline-search';
import { cleanString } from '@utilities/attachments';
import {
  createAtOrSyncedDateFilter,
  dueDateFilter,
  scheduledDateFilter
} from '@utilities/date-filters';
import { sortFilterTags } from '@utilities/filter-is-changed';

export const getFilterPipeline = ({
  filters,
  store,
  sitesMap,
  pipelines,
  SelectedFilter,
  cacheSites
}) => {
  const {
    searched = '',
    pipelineStatus,
    pipelineArea,
    taskType,
    taskStatus,
    priority,
    assign,
    scheduledDate,
    download,
    dueDate: filterDueDate,
    workOrderPriority,
    createdAt,
    syncedDate: syncedDateFilter,
    checkRecurrancePriority,
    inspectionType,
    checkType,
    inspectionRun,
    workOrderType
  } = filters;

  const pipelinesPipelineStatusData = {},
    pipelinesAreaHierarchyTreeData = {},
    pipelinesWorkOrderPriorityDataMap = {};

  const inspectionTypeMap = inspectionType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const inspectionRunMap = inspectionRun?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedRecurrancePriority = checkRecurrancePriority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const workOrderTypeMap = workOrderType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedAreaMap = pipelineArea.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedWorkOrderPriority = workOrderPriority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedPipelineStatusMap = pipelineStatus.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const checkTypeMap = checkType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedTaskType = taskType?.value?.includes('All')
    ? {
        'Work Order': true,
        'Work Request': true,
        Inspection: true,
        Check: true
      }
    : taskType?.value.reduce((acc, curr) => {
        acc[
          curr === 'Work orders'
            ? 'Work Order'
            : curr === 'Work requests'
            ? 'Work Request'
            : curr === 'Inspections'
            ? 'Inspection'
            : curr === 'Check'
            ? 'Check'
            : 'Work Order'
        ] = true;
        return acc;
      }, {});

  const selectedTaskStatus = taskStatus.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedPriority = priority.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const pipelineWithDataPoints = pipelines;

  let filteredPipeline = pipelineWithDataPoints
    .filter((pipeline) => {
      const { status, area, allTasksAndInspectionsForPipeline } = pipeline;

      pipelinesAreaHierarchyTreeData[area] = true;

      pipelinesPipelineStatusData[status] = true;

      if (!searchPipelineData({ pipeline, inputValue: searched, store })) return false;

      if (pipelineArea.value.length > 0 && !selectedAreaMap[area]) return false;
      if (pipelineStatus.value.length > 0 && !selectedPipelineStatusMap[status]) return false;

      const dueCon = _.isArray(filterDueDate.value) ? !filterDueDate.value.includes('') : true;
      const scheduleCon = _.isArray(scheduledDate.value) ? !scheduledDate.value.includes('') : true;
      const createAtCon = _.isArray(createdAt?.value) ? !createdAt?.value.includes('') : true;
      const syncedDateCon = _.isArray(syncedDateFilter?.value)
        ? !syncedDateFilter?.value.includes('')
        : true;

      if (
        taskType.value.length > 0 &&
        !allTasksAndInspectionsForPipeline.some(
          (x) => selectedTaskType[x?.newWorkRequest ? 'Work Request' : x.type]
        )
      )
        return false;

      if (
        taskStatus.value.length > 0 &&
        !allTasksAndInspectionsForPipeline.some((x) => selectedTaskStatus[x.status])
      )
        return false;

      if (
        workOrderType?.value?.length > 0 &&
        !allTasksAndInspectionsForPipeline.some((x) => workOrderTypeMap[x?.typeId])
      )
        return false;

      allTasksAndInspectionsForPipeline.map((x) => {
        if (!pipelinesWorkOrderPriorityDataMap[x.priority])
          pipelinesWorkOrderPriorityDataMap[x.priority] = true;
      });

      if (
        priority.value.length > 0 &&
        !allTasksAndInspectionsForPipeline.some(
          (x) => x.type === 'Work Order' && selectedPriority[x.priority]
        )
      )
        return false;
      if (
        assign?.value?.length > 0 &&
        !allTasksAndInspectionsForPipeline.some((y) =>
          assign.value.some((r) => y?.customAssignedTaskValue?.includes(r))
        )
      )
        return false;

      if (
        workOrderPriority.value.length > 0 &&
        !allTasksAndInspectionsForPipeline.some(
          (x) => x.type === 'Work Order' && selectedWorkOrderPriority[x.priority]
        )
      )
        return false;

      if (
        checkRecurrancePriority?.value?.length > 0 &&
        !allTasksAndInspectionsForPipeline.some(
          (x) => x.type === 'Check' && selectedRecurrancePriority[x.recurrencePriorityId]
        )
      )
        return false;

      if (
        inspectionType?.value?.length > 0 &&
        !allTasksAndInspectionsForPipeline.some((x) => inspectionTypeMap[x.inspectionFormId])
      )
        return false;

      if (
        inspectionRun?.value?.length > 0 &&
        !allTasksAndInspectionsForPipeline.some(
          (x) => inspectionRunMap[`${x.runNumber} - ${x.RunName}`?.trim()]
        )
      )
        return false;

      if (
        checkType?.value.length > 0 &&
        !allTasksAndInspectionsForPipeline.some((x) => checkTypeMap[x.inspectionTypeId])
      )
        return false;

      if (
        !_.isEmpty(filterDueDate.value) &&
        dueCon &&
        !allTasksAndInspectionsForPipeline.some((x) =>
          dueDateFilter({
            taskDueDate: x.dueDate ? x?.dueDate : x?.due ? x?.due : x?.nextDue,
            dueDateFilter: filterDueDate
          })
        )
      )
        return false;

      if (
        !_.isEmpty(scheduledDate.value) &&
        scheduleCon &&
        !allTasksAndInspectionsForPipeline.some((x) =>
          scheduledDateFilter({
            startDate: x.startDate || x?.NextScheduled,
            syncedDate: x.syncedDate,
            dueDateFilter: scheduledDate
          })
        )
      )
        return false;

      if (
        !_.isEmpty(createdAt?.value) &&
        createAtCon &&
        !allTasksAndInspectionsForPipeline.some((x) =>
          createAtOrSyncedDateFilter({
            dateForFilter: x?.createdDate,
            filterSelectedDate: createdAt
          })
        )
      )
        return false;

      if (
        !_.isEmpty(syncedDateFilter?.value) &&
        syncedDateCon &&
        !allTasksAndInspectionsForPipeline.some((x) =>
          createAtOrSyncedDateFilter({
            dateForFilter: x.syncedDate,
            filterSelectedDate: syncedDateFilter
          })
        )
      )
        return false;

      return true;
    })
    .sort((a, b) => {
      switch (SelectedFilter[0]) {
        case 'DUE':
          return dueDateSort(a, b);
        case 'DISTANCE':
          return distanceFilter(a, b, sitesMap, cacheSites);
        case 'A-Z':
          return a.title.localeCompare(b.title);
        case '# TASKS':
          return b?.allotDoneTasks?.length - a?.allotDoneTasks?.length;
        default:
          if (a?.licenseNumber === b?.licenseNumber) return a?.lineNumber - b?.lineNumber;
          else return a?.licenseNumber - b?.licenseNumber;
      }
    })
    ?.map((a) => {
      const dataPoints = a?.dataPointsForSearch || [];
      const cleanInput = a?.dataPointsForSearch?.length > 0 ? cleanString(searched) : '';

      let newDataPointsList = dataPoints.filter((item) => {
        let titleAndValueCombineObject = `${item.title} : ${item.value}`;
        return cleanString(String(titleAndValueCombineObject)).includes(cleanInput);
      });

      let newPoints = {};

      for (let siteId of a?.siteIds || []) {
        const siteDataPoints = a?.siteDataPointsForSearch[siteId] || [];

        const cleanInputForSiteDataPoints =
          a?.siteDataPointsForSearch?.[siteId]?.length > 0 ? cleanString(searched) : '';

        let newDataPointsForSiteList = siteDataPoints.filter((item) => {
          let titleAndValueCombineObject = `${item.title} : ${item.value}`;
          return cleanString(String(titleAndValueCombineObject)).includes(
            cleanInputForSiteDataPoints
          );
        });

        if (
          a?.dataPointsForSite[siteId]?.length > 0 ||
          a?.siteDataPointsForSearch?.[siteId]?.length > 0
        ) {
          if (searched) {
            newPoints = {
              ...newPoints,
              [siteId]: [...a.dataPointsForSite[siteId], ...newDataPointsForSiteList]
            };
          } else {
            newPoints = {
              ...newPoints,
              [siteId]: a.dataPointsForSite[siteId]
            };
          }
        }
      }

      return {
        ...a,
        dataPoints: searched ? [...a.dataPoints, ...newDataPointsList] : a?.dataPoints,
        newPointsForSiteIds: newPoints
      };
    });

  return {
    filteredPipeline,
    pipelinesPipelineStatusData,
    pipelinesAreaHierarchyTreeData,
    pipelinesWorkOrderPriorityDataMap
  };
};

export const getFilterPipelineWithPromise = ({ args }) => {
  return new Promise((resolve) => {
    const response = getFilterPipeline({ ...args });
    resolve(response);
  });
};

const dueDateSort = (a, b) => {
  if (!a.soonestDueDate || !b.soonestDueDate) {
    return a.soonestDueDate ? -1 : 1;
  }

  const dateA = new Date(a.soonestDueDate);
  const dateB = new Date(b.soonestDueDate);

  if (dateA < dateB) {
    return -1;
  } else if (dateA > dateB) {
    return 1;
  } else {
    return 0;
  }
};

const distanceFilter = (a, b, sitesMap, cacheSites) => {
  return (
    (cacheSites[sitesMap[a?.siteIds[0]]]?.distance != null
      ? cacheSites[sitesMap[a?.siteIds[0]]]?.distance
      : Infinity) -
      (cacheSites[sitesMap[b?.siteIds[0]]]?.distance != null
        ? cacheSites[sitesMap[b?.siteIds[0]]]?.distance
        : Infinity) || a?.name?.localeCompare(b.name)
  );
};

export const tagsDataList = ['DISTANCE', '# TASKS', 'DUE'];

export const mapValues = ({
  pipelinesAreaHierarchyTreeDataMap,
  pipelinesPipelinesStatusDataMap,
  pipelinesWorkOrderPriorityDataMap,
  pipelines = [],
  store
}) => {
  const { picklists, tasks: reduxTasks, checks, inspection } = store;
  const { workOrderUsersGroupsMap = {}, tasksList = [] } = reduxTasks;
  const { checkTypesMap } = checks;

  const {
    inspectionTypesMap = {},
    inspectionFormsUserCacheTypeMap = {},
    inspectionFormsUserCacheMap = {}
  } = inspection;

  const {
    pipelineStatusData = [],

    workOrderPriorityData = [],
    areaHierarchyTreeData = [],
    picklistsMap = {}
  } = picklists;

  let pipelineStatusTags = pipelinesPipelinesStatusDataMap
    ? pipelineStatusData?.filter(({ key }) => pipelinesPipelinesStatusDataMap[key])
    : pipelineStatusData;

  pipelineStatusTags = sortFilterTags({
    list: pipelineStatusTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let workOrderPriorityTags = pipelinesWorkOrderPriorityDataMap
    ? workOrderPriorityData.filter(({ key }) => pipelinesWorkOrderPriorityDataMap[key])
    : workOrderPriorityData;

  workOrderPriorityTags = sortFilterTags({
    list: workOrderPriorityTags,
    isCheckField: true,
    fieldName: 'value'
  });

  const assignedUserGroups = getPipelineTaskAssignedUserGroups(pipelines);
  const assignableUsersTags = getTaskAssignedToTags({
    assignedUserGroups,
    workOrderUsersGroupsMap
  });

  const treeData = pipelinesAreaHierarchyTreeDataMap
    ? removeUnavailableTreeData(
        JSON.parse(JSON.stringify(areaHierarchyTreeData)),
        pipelinesAreaHierarchyTreeDataMap
      )
    : areaHierarchyTreeData;

  const taskTypeTag = getPipelinesTaskTypeTags(pipelines);
  const taskStatusTags = getPipelineTaskStatusTags(pipelines);

  const checkRecurrancePriorityTags = getCheckRecurrancePriorityTags(pipelines, picklistsMap);
  const { checkTypeTags, checkTypeTagsMap } = getCheckTypeTags(pipelines, checkTypesMap);

  const inspectionRunTags = getInspectionRunTags(pipelines);

  const { workOrderTypeTags, workOrderTypeTagMap } =
    getWorkOrderTypeTags(pipelines, picklistsMap) || [];
  const taskRecurringTemplateTags = getTaskRecurringTemplateTags(pipelines) || [];

  const { inspectionTypeTags, inspectionTypeTagsMap } = getInspectionTypeTags(
    pipelines,
    inspectionTypesMap,
    inspectionFormsUserCacheTypeMap,
    inspectionFormsUserCacheMap
  );

  return {
    assignableUsersTags,
    treeData,
    taskTypeTag,
    taskStatusTags,
    pipelineStatusTags,
    workOrderPriorityTags,
    checkRecurrancePriorityTags,
    checkTypeTags,
    inspectionRunTags,
    taskRecurringTemplateTags,
    checkTypeTagsMap,
    inspectionTypeTags,
    inspectionTypeTagsMap,
    workOrderTypeTags,
    workOrderTypeTagMap
  };
};

const getWorkOrderTypeTags = (pipelines = [], picklistsMap = {}) => {
  let workOrderTypeTags = [];

  pipelines.map((item) =>
    item?.allTasksAndInspectionsForPipeline?.map((task) => {
      if (picklistsMap[task?.typeId] && !workOrderTypeTags.includes(picklistsMap[task?.typeId]))
        workOrderTypeTags.push(picklistsMap[task?.typeId]);
    })
  );

  const workOrderTypeTagMap = workOrderTypeTags?.reduce((acc, curr) => {
    acc[curr.key] = { value: curr.value };
    return acc;
  }, {});

  workOrderTypeTags = sortFilterTags({
    list: workOrderTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return { workOrderTypeTags, workOrderTypeTagMap };
};

const getInspectionRunTags = (pipelines = []) => {
  const tasks = pipelines.flatMap((x) => x.allTasksAndInspectionsForPipeline);

  const tags = Array.from(
    new Set(
      tasks.map((task) => {
        if (task?.runNumber) return `${task.runNumber} - ${task?.RunName?.trim()}`;
      })
    )
  ).filter((y) => y);
  return sortFilterTags({
    list: tags,
    isCheckField: false,
    fieldName: ''
  });
};

const getInspectionTypeTags = (
  pipelines = [],
  inspectionTypesMap = {},
  inspectionFormsUserCacheTypeMap = {},
  inspectionFormsUserCacheMap = {}
) => {
  const tasks = pipelines.flatMap((x, i) => x.allTasksAndInspectionsForPipeline);
  let inspectionTypeTags = [];

  for (let task of tasks) {
    const inspectionFormId = task?.inspectionFormId || '';
    let inspectionFormName = inspectionFormsUserCacheMap[inspectionFormId] || '';

    const original = inspectionTypesMap[task?.inspectionTypeId];
    if (
      inspectionFormName &&
      !inspectionTypeTags.some((tag) => tag.value === inspectionFormName?.name)
    ) {
      const obj = {
        ...original,
        value: inspectionFormName?.name,
        inspectionFormId: inspectionFormName?.inspectionFormId,
        key: inspectionFormName?.inspectionFormId
      };
      inspectionTypeTags.push(obj);
    }
  }

  const inspectionTypeTagsMap = inspectionTypeTags?.reduce((acc, curr) => {
    acc[curr.inspectionFormId] = { value: curr.value };
    return acc;
  }, {});

  inspectionTypeTags = sortFilterTags({
    list: inspectionTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return { inspectionTypeTags, inspectionTypeTagsMap };
};

const getTaskRecurringTemplateTags = (pipelines = []) => {
  let tags = Array.from(
    new Set(
      pipelines
        .map(
          (item) =>
            item.allTasksAndInspectionsForPipeline &&
            item.allTasksAndInspectionsForPipeline.map((task) => {
              if (task.rtNumber) return `${task.rtNumber} - ${task.rtName}`;
            })
        )
        .flat()
        .filter((name) => name)
    )
  );

  tags = sortFilterTags({
    list: tags,
    isCheckField: false,
    fieldName: ''
  });

  return tags;
};

const getCheckTypeTags = (equipments = [], checkTypesMap = {}) => {
  let checkTypeTags = [];

  for (let equipment of equipments) {
    const equipmentTask = equipment?.allTasksAndInspectionsForPipeline || [];
    for (let task of equipmentTask) {
      if (task?.type === 'Check' && checkTypesMap[task?.inspectionTypeId]) {
        if (
          checkTypesMap[task?.inspectionTypeId] &&
          !checkTypeTags.includes(checkTypesMap[task?.inspectionTypeId])
        )
          checkTypeTags.push(checkTypesMap[task?.inspectionTypeId]);
      }
    }
  }

  const checkTypeTagsMap = checkTypeTags?.reduce((acc, curr) => {
    acc[curr.key] = { value: curr.value };
    return acc;
  }, {});

  checkTypeTags = sortFilterTags({
    list: checkTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return { checkTypeTags, checkTypeTagsMap };
};

const getCheckRecurrancePriorityTags = (equipments = [], picklistsMap) => {
  let checkRecurrenceTags = [];

  for (let equipment of equipments) {
    const equipmentTask = equipment?.allTasksAndInspectionsForPipeline || [];
    for (let task of equipmentTask) {
      if (
        task?.type === 'Check' &&
        task?.recurrencePriorityId &&
        picklistsMap[task?.recurrencePriorityId]
      ) {
        let recurreanceValue = picklistsMap[task?.recurrencePriorityId] || {};
        let recurreanceValueCopy = { ...recurreanceValue };
        recurreanceValueCopy.value = recurreanceValue?.value?.split(' ')[0] || '';
        if (!checkRecurrenceTags.some((tag) => tag.value === recurreanceValueCopy.value)) {
          checkRecurrenceTags.push(recurreanceValueCopy);
        }
      }
    }
  }

  checkRecurrenceTags = sortFilterTags({
    list: checkRecurrenceTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return checkRecurrenceTags;
};

const getTaskAssignedToTags = ({ assignedUserGroups, workOrderUsersGroupsMap }) => {
  let data =
    (assignedUserGroups && // responsible users groups
      _.size(assignedUserGroups) > 0 &&
      assignedUserGroups
        .map((key) => workOrderUsersGroupsMap[key] && workOrderUsersGroupsMap[key]?.name)
        .filter((y) => y)) ||
    [];

  let assignableUsersTags = ['Me, directly', 'A group I’m in', ...data];

  return assignableUsersTags;
};
const getPipelineTaskStatusTags = (pipelines) => {
  let taskStatusTags = [];
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.status === 'To do')
    ) {
      taskStatusTags.push('To do');
      break;
    }
  }
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.status === 'In progress')
    ) {
      taskStatusTags.push('In progress');
      break;
    }
  }
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.status === 'Pending sync')
    ) {
      taskStatusTags.push('Pending sync');
      break;
    }
  }
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.status === 'Synced')
    ) {
      taskStatusTags.push('Synced');
      break;
    }
  }
  return taskStatusTags;
};

const getPipelineTaskAssignedUserGroups = (pipelines) => {
  let assignedUserGroups = [];
  for (let pipeline of pipelines) {
    if (_.size(pipeline.allTasksAndInspectionsForPipeline) > 0) {
      assignedUserGroups.push(
        Array.from(
          new Set(
            pipeline.allTasksAndInspectionsForPipeline.map((t) => t?.responsibleUserGroups).flat(2)
          )
        ) || []
      );
    }
  }
  return Array.from(new Set(_.flattenDeep(assignedUserGroups))) || [];
};

const getPipelinesTaskTypeTags = (pipelines) => {
  let taskTypeTag = [];
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.type === 'Work Order')
    ) {
      taskTypeTag.push('Work orders');
      break;
    }
  }
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some(
        (x) => x.type === 'Work Request' || x.newWorkRequest
      )
    ) {
      taskTypeTag.push('Work requests');
      break;
    }
  }
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.type === 'Inspection')
    ) {
      taskTypeTag.push('Inspections');
      break;
    }
  }
  for (let pipeline of pipelines) {
    if (
      _.size(pipeline.allTasksAndInspectionsForPipeline) > 0 &&
      pipeline.allTasksAndInspectionsForPipeline.some((x) => x.type === 'Check')
    ) {
      taskTypeTag.push('Check');
      break;
    }
  }
  return taskTypeTag?.length > 0 ? [...taskTypeTag, 'All'] : taskTypeTag;
};

export const countFilters = (filters, inputValue, store) => {
  let count = 0;
  const { picklists } = store;

  const {
    taskType,
    taskStatus,
    assign,
    priority,
    pipelineArea,
    pipelineStatus,
    workOrderPriority = {},
    checkRecurrancePriority = { value: [] },
    inspectionType = { value: [] },
    inspectionRun = { value: [] },
    checkType = { value: [] }
  } = filters;

  if (inputValue?.trim()) ++count;

  if (assign?.value?.length > 0) ++count;
  if (inspectionType?.value?.length > 0) ++count;
  if (inspectionRun?.value?.length > 0) ++count;
  if (taskType?.value?.length > 0) ++count;
  if (checkType?.value?.length > 0) ++count;
  if (taskStatus?.value?.length > 0) ++count;
  if (priority?.value?.length > 0 || workOrderPriority?.value?.length > 0) ++count;
  if (pipelineStatus?.value?.length > 0) ++count;
  if (
    pipelineArea?.value &&
    pipelineArea?.value.filter((el) => picklists.areaHierarchyTreeDataMap?.[el])?.length > 0
  )
    ++count;

  ['dueDate', 'scheduledDate', 'createdAt', 'syncedDate'].forEach((key) => {
    const { value, selected } = filters[key] || { value: '', selected: '' };
    if (
      selected &&
      !_.isEmpty(value) &&
      (selected === 'on'
        ? value !== ''
        : selected === 'range'
        ? value?.[0] !== '' && value?.[0] !== ''
        : value?.length > 0)
    )
      ++count;
  });
  return count;
};

export const getPipelinesDefaultFilters = {
  searched: '',
  pipelineArea: { value: [] },
  pipelineStatus: { value: [] },
  taskType: { value: [] },
  taskStatus: { value: [] },
  assign: { value: [] },
  priority: { value: [] },
  workOrderPriority: { value: [] },

  download: { value: '', selected: 'past' },
  createdAt: { value: '', selected: 'past' },
  syncedDate: { value: '', selected: 'past' },
  dueDate: { value: [], selected: 'next' },
  scheduledDate: { value: [], selected: 'next' },
  checkRecurrancePriority: { value: [] },
  inspectionType: { value: [] },
  checkType: { value: [] }
};
